<template>
  <v-row class="pl-3 pt-3">
    <v-dialog max-width="400" v-model="dialog">
      <v-card rounded="lg">
        <v-card-title class="justify-space-between align-start pb-0">
          <div>
            <h3 class="text-h6 font-weight-medium">Edit Loan Details</h3>
            <p class="text-subtitle-2 text--secondary">
              Enter the details below and submit
            </p>
          </div>
        </v-card-title>

        <v-card-text>
          <ValidationObserver ref="itemObserver">
            <v-row dense>
              <v-col cols="12">
                <ValidationProvider
                  name="Current Balance"
                  :rules="`required|min_value:0|max_value:${selectedItem.loanAmount}`"
                  events="['blur']"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    dense
                    type="number"
                    min="0"
                    max="100"
                    outlined
                    label="Current Balance*"
                    v-model.number="selectedItem.currentBalance"
                    :error-messages="errors"
                  ></v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  :name="`${selectedItem.frequencyNoun}(s) Remaining`"
                  :rules="`required|min_value:0`"
                  events="['blur']"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    dense
                    type="number"
                    min="0"
                    max="100"
                    outlined
                    label="Tenure Remaining*"
                    v-model.number="selectedItem.tenureRemaining"
                    :error-messages="errors"
                  >
                    <span slot="append" style="font-size: 1em" class="pt-1">
                      {{
                        !loan.frequencyNoun ? '-' : loan.frequencyNoun + '(s)'
                      }}
                    </span>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
            </v-row>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions class="pt-0 justify-end">
          <v-btn
            @click="dialog = false"
            min-width="120"
            depressed
            class="text-capitalize"
            >Cancel</v-btn
          >
          <v-btn
            @click="onSubmit()"
            min-width="120"
            depressed
            color="primary"
            class="text-capitalize"
            >Submit</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-col cols="12">
      <h3 class="text-h6 grey--text text--darken-3 font-weight-bold mb-7">
        Loans active on this account
      </h3>

      <!-- Alert -->
      <v-alert
        dense
        tile
        dismissible
        v-if="alertState.show"
        :type="alertState.type"
        class="ma-0"
      >
        {{ alertState.message }}
      </v-alert>

      <v-card outlined>
        <v-data-table
          :loading="loading"
          hide-default-footer
          :headers="headers"
          :items="activeLoans"
        >
          <template #no-data>
            <h2 class="mb-2 subtitle-1">No loans found</h2>
          </template>
          <template v-slot:[`item.selected`]="{ item, index }">
            <v-checkbox
              v-model="item.isSelected"
              @change="onCheckboxClicked(index, item)"
            />
          </template>
          <template v-slot:[`item.loanProduct`]="{ item }">
            {{ item.loanProductDetails?.loanProductName || item.loanProduct }}
          </template>
          <template v-slot:[`item.loanAmount`]="{ item }">
            {{ asMoney(item.loanAmount) }}
          </template>
          <template v-slot:[`item.currentBalance`]="{ item }">
            {{ asMoney(item.currentBalance) }}
          </template>
          <template v-slot:[`item.interest`]="{ item }">
            {{ asPercent(item.interest) }}
          </template>
          <template v-slot:[`item.shares`]="{ item }">
            {{ asPercent(item.shares) }}
          </template>
          <template v-slot:[`item.frequencyPayment`]="{ item }">
            {{ asMoney(item.frequencyPayment) }}
          </template>
          <template v-slot:[`item.tenureRemaining`]="{ item }">
            {{ item.tenureRemaining }}
            {{ item.frequencyNoun ? `${item.frequencyNoun}(s)` : '-' }}
          </template>

          <!-- Custom slot for edit button -->
          <template #[`item.actions`]="{ item, index }">
            <DataTableActions
              :actions="dataTableActions"
              :data="{ index, item }"
            />
          </template>
        </v-data-table>
      </v-card>

      <v-row class="mt-5">
        <v-col cols="12" md="4">
          <v-card outlined>
            <v-card-title
              class="text-subtitle-1 font-weight-bold text--secondary"
              >Current Loan Terms</v-card-title
            >
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <p>Interest</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.currentPolicy.interest) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Shares</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.currentPolicy.shares) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Tenure Remaining</p>
                <p class="font-weight-medium">
                  {{ this.policies.currentPolicy.tenureRemaining || '-' }}
                  {{ loan.frequencyNoun }}(s)
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined>
            <v-card-title
              class="text-subtitle-1 font-weight-bold text--secondary"
              >Previous Loan Terms</v-card-title
            >
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <p>Interest</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.previousPolicy.interest) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Shares</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.previousPolicy.shares) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Tenure Remaining</p>
                <p class="font-weight-medium">
                  {{ this.policies.previousPolicy.tenureRemaining || '-' }}
                  {{ loan.frequencyNoun }}(s)
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="4">
          <v-card outlined>
            <v-card-title
              class="text-subtitle-1 font-weight-bold text--secondary"
              >Weighted Loan Terms</v-card-title
            >
            <v-card-text>
              <div class="d-flex justify-space-between align-center">
                <p>Interest</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.weightedPolicy.interest) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Shares</p>
                <p class="font-weight-medium">
                  {{ asPercent(this.policies.weightedPolicy.shares) }}
                </p>
              </div>
              <div class="d-flex justify-space-between align-center">
                <p>Tenure Remaining</p>
                <p class="font-weight-medium">
                  {{ this.policies.weightedPolicy.tenureRemaining || '-' }}
                  {{ loan.frequencyNoun }}(s)
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script lang="js">
import { mapGetters } from 'vuex';
import FormatMixin from '@/mixins/format.mixin';
import CoreMixin from '@/mixins/core.mixin';
import { VuetifyMoneyOptions } from '@/lib/constants/options';
import { LoanPoolService } from '@/modules/loanpro/services/loan-pool.service';
import DataTableActions from '@/modules/shared/components/core/datatable/DataTableActions.vue';
import { CoreIntegrationService } from '@/modules/integrations/services/core/core-integration.service';

export default {
  mixins: [FormatMixin, CoreMixin],
  components: { DataTableActions },
  props: {
    loanDetails: {
      type: Object,
      default: () => ({
        loanTerms: {},
        loanPurposes: [],
        loanProductDetails: {
          maximumAge: 1,
        },
      }),
    }
  },

  watch: {
    loanDetails: {
      deep: true,
      immediate: true,
      handler: function (val) {
        this.loan = structuredClone(val);
        this.calculateTerms();
      },
    }
  },

  computed: {
    ...mapGetters("loanSettingsModule", [
      'loanProducts',
      'branches',
      'paymentFrequencies',
      'riskFactors',
    ]),
  },

  data() {
    return {
      dialog: false,
      loading: false,

      alertState: {
        type: 'error',
        show: false,
        message: ''
      },

      // Actions for edit button in datatable
      dataTableActions: [
        {
          icon: 'mdi-pencil-outline',
          action: 'Edit',
          actionCallBack: (index, item) => {
            this.selectedIndex = index;
            this.selectedItem = item;
            this.dialog = true;
          }
        }
      ],
      activeLoans: [],
      previousAccountNumber: '',

      selectedItem: {},
      selectedIndex: -1,

      policies: {
        currentPolicy: {},
        previousPolicy: {},
        weightedPolicy: {},
        finalPolicy: {}
      },

      loan: {
        loanTerms: {},
        loanPurposes: [],
        loanProductDetails: {
          maximumAge: 1,
        },
      },

      VuetifyMoneyOptions: VuetifyMoneyOptions,

      /** @type {import('vuetify').DataTableHeader[]} */
      headers: [
        { text: 'Selected', value: 'selected', sortable: false },
        { text: 'Product', value: 'loanProduct' },
        { text: 'Original Amount', value: 'loanAmount' },
        { text: 'Current Balance', value: 'currentBalance' },
        { text: 'Interest', value: 'interest' },
        { text: 'Shares', value: 'shares' },
        { text: 'Payment Frequency', value: 'frequencyPayment' },
        { text: 'Tenure Remaining', value: 'tenureRemaining' },
        { text: 'Actions', value: 'actions' }
      ]
    };
  },

  methods: {
    validate() {
      const selected = !!this.activeLoans.filter((el) => el.isSelected).length
      if(!selected) {
        return { valid: false, message: "Please selected at least one loan for consolidation/top-up" }
      }
      return { valid: true }
    },

    onSubmit() {
      if(this.selectedItem.currentBalance <= 0 || this.selectedItem.tenureRemaining <= 0 ) {
        this.showSnack({
          color: 'error',
          title: `Item Selected is invalid.`,
          message: `Both current balance and tenure remaining should be greater than zero (0).`,
        });
        this.selectedItem.isSelected = false;
        this.activeLoans.splice(index, 1, item)
        return;
      }

      this.dialog = false;
      this.activeLoans.splice(this.selectedIndex, 1, this.selectedItem);
      this.selectedItem = {};
      this.calculateTerms();

      this.$emit('consolidationUpdated', {
        totalConsolidatedAmount: this.loan.totalConsolidatedAmount,
        loans: this.activeLoans.filter(el => el.isSelected ),
        policies: this.policies
      })
    },

    onCheckboxClicked(index, item) {
      if(item.currentBalance <= 0 || item.tenureRemaining <= 0 ) {
        this.showSnack({
          color: 'error',
          title: `Item Selected is invalid.`,
          message: `Both current balance and tenure remaining should be greater than zero (0).`,
        });
        item.isSelected = false;
        this.activeLoans.splice(index, 1, item)
        return;
      }

      if (this.loan.loanCreationType?.toLowerCase() === "top up") {
        this.activeLoans.forEach((el, i) => {
          if (el.isSelected && i !== index) {
            el.isSelected = false;
          }
        });
      }
      this.calculateTerms();

      this.$emit('consolidationUpdated', {
        totalConsolidatedAmount: this.loan.totalConsolidatedAmount,
        loans: this.activeLoans.filter(el => el.isSelected ),
        policies: this.policies
      })
    },

    async updateLoansViaIntegration() {
      //Reset state
      this.alertState.show = false;

      try {

        this.alertState.type = 'info';
        this.alertState.show = true;
        this.alertState.message = 'Updating Loan Details...';

        //Get loans for person on core
        const coreIntegrationService = new CoreIntegrationService();
        const coreId = this.loan.loanDistributions.filter(el => el.distributionType === 'internal')?.[0]?.person?.coreId;
        const { data: res } = await coreIntegrationService.getPersonLoans(coreId);
        const loans = res.data?.loans || [];

        //Update loans in system
        if(loans.length > 0) {
          const loanService = new LoanPoolService();

          await loanService.integrationBatchUpdateLoan(loans.map((loan) => ({
            maturityDate: loan.maturity_date,
            dateDisbursed: loan.start_date,
            currentBalance: +loan.current_balance,
            interest: +loan.frequency_interest,
            loanAmount: +loan.requested_loan,
            frequencyNoun: loan.frequency_noun,
            frequencyName: loan.frequency_name,
            frequencyPayment: loan.frequency_payment,
          })));
        }

        this.alertState.show = false;
      } catch(e) {
        console.log(e)
        this.showSnack({
          color: 'error',
          title: `Something went wrong trying to update loan details`,
          message: ''
        });
      } finally {
      }
    },

    async getPreviousLoans() {
      try {
        this.loading = true;
        if(this.activeLoans.length == 0 || this.loan.mainAccountNumber !== this.previousAccountNumber) {
          this.previousAccountNumber = this.loan.mainAccountNumber;

          await this.updateLoansViaIntegration();

          const loanService = new LoanPoolService();
          const previousLoansResponse = await loanService.searchLoan({ loanProgresses: ["disbursed"], search: this.loan.mainAccountNumber })
          this.activeLoans = previousLoansResponse.data?.data.map((el) => {
            const maturityDate = new Date(el.maturityDate);
            let remainingMonths =
              (maturityDate.getTime() - new Date().getTime()) / 1000;
            remainingMonths = remainingMonths / (60 * 60 * 24 * 7 * 4);
            remainingMonths = Math.abs(Math.round(remainingMonths));

            el.tenureRemaining =
              isNaN(remainingMonths) || remainingMonths < 0
                ? 0
                : remainingMonths;

            el.tenureRemaining =
              Math.ceil((el.tenureRemaining / 12) * Number(el.frequencyPeriod || 12));

            el.interest = Number(el.interest || 0);
            el.shares = Number(el.shares || 0);

            console.log(el)
            return {
              id: el.id,
              loanProduct: el.loanProduct,
              loanProductDetails: el.loanProductDetails || {},
              tenureRemaining: el.tenureRemaining,
              interest: el.interest,
              shares: el.shares,
              currentBalance: el.currentBalance,
              loanAmount: +el.loanAmount,
              frequencyPayment: +el.frequencyPayment,
              frequencyPeriod: el.frequencyPeriod,
              frequencyNoun: el.frequencyNoun,
            };
          });
        }

        if(this.loan.loanCreationType === "Top Up") {
          const product = this.loan.loanProductDetails || {};
          this.activeLoans = this.activeLoans.filter((el) => el.loanProductDetails.id === product.id)
        }

        this.activeLoans = this.activeLoans
          .filter((el) => el.id != this.loan.id)
          .map((al) => {
            const loan = this.loan.loansToConsolidate?.find((el) => al.id === el.id)

            if(loan) {
              al.isSelected = true;
              al.currentBalance = loan.currentBalance;
              al.tenureRemaining = loan.tenureRemaining || 0;
              al.frequencyPayment = loan.frequencyPayment || 0;
              al.frequencyPeriod = loan.frequencyPeriod || this.loan.frequencyPeriod;
              al.frequencyNoun = loan.frequencyNoun || this.loan.frequencyNoun;
            }
            return al;
          });

      } catch(e) {
        this.httpErrorHandler(e, { title: "Unable to get previous loans" });
      } finally {
        this.calculateTerms();
        this.loading = false;
      }
    },

    getLoanAmount () {
      return +this.loan.totalInternalLoanAmount || 0 +
        +this.loan.totalExternalLoanAmount || 0
    },

    calculateTerms() {
      this.resetPolicies();

      const totalLoanAmount = this.getLoanAmount()
      this.loan.totalConsolidatedAmount = 0;

      //Calculate total pervious terms
      this.activeLoans.forEach((el, i) => {
        if (el.isSelected) {
          this.policies.previousPolicy.currentBalance += Number(
            el.currentBalance
          );
          this.policies.previousPolicy.interest += +el.currentBalance * el.interest;
          this.policies.previousPolicy.shares += +el.currentBalance * el.shares;
          this.policies.previousPolicy.tenureRemaining += Math.ceil(+el.currentBalance * el.tenureRemaining);

          el.frequencyPeriod = this.loan.frequencyPeriod;
          el.frequencyNoun = this.loan.frequencyNoun;

          this.activeLoans.splice(i, 1, el);
          this.loan.totalConsolidatedAmount += +el.currentBalance;
        }
      });

      //Calculate weighted pervious terms
      this.policies.previousPolicy.interest =
        +this.policies.previousPolicy.interest /
        (+this.policies.previousPolicy.currentBalance || 1);
      this.policies.previousPolicy.shares =
        +this.policies.previousPolicy.shares /
        (+this.policies.previousPolicy.currentBalance || 1);
      this.policies.previousPolicy.tenureRemaining =
        Math.ceil(+this.policies.previousPolicy.tenureRemaining /
        (+this.policies.previousPolicy.currentBalance || 1));

      //Get Current Terms
      this.policies.currentPolicy.currentBalance = +totalLoanAmount;
      this.policies.currentPolicy.interest = +this.loan.interest;
      this.policies.currentPolicy.shares = +this.loan.shares;
      this.policies.currentPolicy.tenureRemaining = +this.loan.frequencyTenure;

      for(const key in this.policies.weightedPolicy) {
        this.policies.weightedPolicy[key] =
          +(this.policies.currentPolicy.currentBalance +
          (this.policies.previousPolicy[key] || 0.0)).toFixed(2);

        if(key === 'currentBalance')  continue;

        this.policies.weightedPolicy[key] =
          +(this.policies.currentPolicy.currentBalance *
          (this.policies.currentPolicy[key] || 0.0)).toFixed(2);

        this.policies.weightedPolicy[key] +=
          +(this.policies.previousPolicy.currentBalance *
          (this.policies.previousPolicy[key] || 0.0)).toFixed(2);

        this.policies.weightedPolicy[key] =
          +(this.policies.weightedPolicy[key] /
          (this.policies.weightedPolicy.currentBalance || 1)).toFixed(2);

        if(key === 'tenureRemaining')  {
          this.policies.weightedPolicy[key] = Math.ceil(this.policies.weightedPolicy[key])
        }
      }

      const product = this.loan.loanProductDetails;

      if (this.loan.loanCreationType == "New") {
        this.policies.finalPolicy = structuredClone(this.policies.currentPolicy);
      } else {
        switch (product.loanPolicyTermsToUse) {
          case "current":
            this.policies.finalPolicy = structuredClone(
              this.policies.currentPolicy
            );
            break;
          case "previous":
            this.policies.finalPolicy = structuredClone(
              this.policies.previousPolicy
            );
            break;
          case "weighted":
            this.policies.finalPolicy = structuredClone(
              this.policies.weightedPolicy
            );
            break;
        }
      }

      /*this.policies.frequencyPolicy = structuredClone(this.policies.finalPolicy);

      this.policies.frequencyPolicy.interest =
        this.policies.finalPolicy.interest /
        Number(this.item.frequency_period || 1);

      this.policies.frequencyPolicy.tenureRemaining = Math.ceil(
        (this.policies.finalPolicy.tenureRemaining / 12) *
          Number(this.loan.frequencyPeriod || 1)
      );

      this.item.loanAmount = this.item.loanAmount + Number(
        this.policies.weightedPolicy.currentBalance
      );

      // this.policies.finalPolicy.tenure =
      this.policies.finalPolicy.currentBalance = this.item.loanAmount; */

    },

    resetPolicies() {
      const defaultPolicy = {
        currentBalance: 0.0,
        interest: 0.0,
        shares: 0.0,
        tenureRemaining: 0,
      };

      this.policies = {
        currentPolicy: { ...defaultPolicy},
        previousPolicy: { ...defaultPolicy} ,
        weightedPolicy: {...defaultPolicy},
        finalPolicy: { ...defaultPolicy}
      };
    },
  },

};
</script>
